// import { store } from '@/store';
import {
  CLIENT_TYPE_PAYOR,
  CLIENT_TYPE_PROPOSER,
  ProductLabel,
  SELF_ID,
} from '@/constants';
import { PaymentMode } from '@/enums/PaymentMode';
import { store } from '@/store';
import { ApplicantPersonalInfo } from '@/types/ApplicantDetails';
import { InitPaymentRequest } from '@/types/Payment';
import { PlanSummaryState } from '@/types/PlanSummary';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInterceptorInstance from './axiosInterceptorInstance';

export const getPaymentType = createAsyncThunk(
  'payment/getPaymentType',
  async ({
    agentCode,
    entityType,
  }: {
    agentCode: string;
    entityType: number;
  }) => {
    try {
      const queryString = `?agentCode=${agentCode}%entityType=${entityType}`;
      const relativeUrl = `Payment/GetPaymentType`;
      const apiUrl = `/application/?relativeUrl=${relativeUrl}&queryString=${queryString}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const getPaymentTransactionNumber = createAsyncThunk(
  'payment/getPaymentTransactionNumber',
  async (encodedOrderId: string) => {
    try {
      const queryString = `?encodedOrderId=${encodedOrderId}`;
      const relativeUrl = 'Payment/GetPaymentTransactionNumber';
      const apiUrl = `/application/?relativeUrl=${relativeUrl}&queryString=${queryString}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

function getMainApplicant(
  planSummary: PlanSummaryState,
): ApplicantPersonalInfo | null {
  const payor = planSummary.applicants.find(
    (app) => app.clientType === CLIENT_TYPE_PAYOR,
  );
  const proposer = planSummary.applicants.find(
    (app) => app.clientType === CLIENT_TYPE_PROPOSER,
  );

  const payorDetail = payor || proposer;

  if (payorDetail) {
    return payorDetail;
  }

  return null;
}

export const initPayment = createAsyncThunk(
  'payment/init',
  async ({
    productType,
    entityType,
    language,
  }: {
    productType: string;
    entityType: number;
    language: string;
  }) => {
    try {
      const rootState = store.getState();
      const planSummary = rootState.planSummary;
      const payMode = planSummary.paymentMode;
      const totalPay =
        payMode === PaymentMode.Monthly
          ? planSummary.premiumAmount.monthlyTotalPremium
          : planSummary.premiumAmount.yearlyTotalPremium;
      const mainApplicant = getMainApplicant(planSummary);

      const refNo = planSummary.applicationsApplicants?.applicants.find(
        (appl) => appl.id === SELF_ID,
      )?.proposalNumber;

      const request: InitPaymentRequest = {
        referenceNo: refNo,
        transactionNo: refNo,
        paymentType: 'CARD',
        amount: totalPay,
        description: ProductLabel[productType],
        customerName: mainApplicant?.name.fullName ?? '',
        email: mainApplicant?.emails.at(0)?.value,
        phone: mainApplicant?.contacts.at(0)?.value.replace(/^\+6/, '') ?? '',
        entityType,
        productType,
        language,
        address1: mainApplicant?.addresses.at(0)?.addr1 ?? '',
        address2: mainApplicant?.addresses.at(0)?.addr2 ?? '',
        address3: mainApplicant?.addresses.at(0)?.addr3 ?? '',
        postcode: mainApplicant?.addresses.at(0)?.postcode ?? '',
        city: mainApplicant?.addresses.at(0)?.city ?? '',
        state: mainApplicant?.addresses.at(0)?.stateDesc ?? '',
        country: mainApplicant?.addresses.at(0)?.countryDesc ?? '',
        refID: planSummary.referenceId,
        isRecurring: true,
      };
      const relativeUrl = 'SubmitPayment';
      const apiUrl = `/payment/${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

type PaymentStatusResponse = {
  Status: 'success' | 'failed';
  Message?: string;
  TransactionDateTime: string;
  BankReferenceNo: string;
};

export const getPaymentStatus = createAsyncThunk<
  PaymentStatusResponse,
  {
    status: string;
    approvalCode: string;
    approvalDate: string;
  },
  {}
>(
  'payment/getPaymentStatus',
  async ({
    status,
    approvalCode,
    approvalDate,
  }: {
    status: string;
    approvalCode: string;
    approvalDate: string;
  }) => {
    if (status === 'success') {
      return {
        Status: 'success',
        Message: 'Payment collection details found in state.',
        TransactionDateTime: approvalDate,
        BankReferenceNo: approvalCode,
      } as PaymentStatusResponse;
    } else {
      // Mock pending/incomplete response
      return {
        Status: 'failed',
        Message: 'No payment collection details found in state.',
      } as PaymentStatusResponse;
    }
  },
);
